/**
 * @callback Router~routeCallback
 * @param {Object.<string, string>} attributes Derived attributes
 * @param {Object.<string, string>} queryParams Query parameters
 * @param {string} fragmentIdentifier Fragment identifier
 */

/**
 * @typedef {Object} Router~Route
 * @property {string} path
 * @property {Router~routeCallback} callback
 * @property {string} name
 */

/**
 * @typedef {Object} Router~RouteResult
 * @property {Router~Route} matchedRoute
 * @property {Object.<string, string>} matchedParams
 */

class Router {
	/**
	 * Create a router
	 * @param {string} basePath
	 */
	constructor(basePath = '/')
	{
		this.current = '';
		this.basePath = basePath.replace(/\/$/, '') + '/';
		this.routes = [];
		this.queryParams = new URLSearchParams;
		window.addEventListener('popstate', () => {
			this.dispatch();
		});
	}

	/**
	 * @param {string} path
	 * @param {Router~routeCallback} callback
	 * @param {string} name
	 * @return {Router}
	 */
	add(path, callback, name = '')
	{
		this.routes.push({path, callback, name});
		return this;
	}

	trimSlashes(path)
	{
		return path
			.toString()
			.replace(/\/$/, '')
			.replace(/^\//, '');
	}

	/**
	 * @param {string} name Route name
	 * @param {Object.<string, string>} substitutions
	 * @return {string}
	 */
	generateUri(name, substitutions)
	{
		for (let i in this.routes) {
			const route = this.routes[i];
			let uri = route.path;

			if (name == route.name) {
				for (let key in substitutions) {
					const value = substitutions[key];
					let matchCount = 0;

					uri = uri.replace(new RegExp('{' + key + '(:[^}]+)?}', 'g'), function () {
						matchCount += 1;
						return value;
					});

					if (uri === null || matchCount === 0) {
						throw new Error('Route `' + name + '` has no such parameter `' + key + '`.');
					}
				}

				if (uri.match(/{([^:}]+)(:[^}]+)?}/)) {
					throw new Error('Too few parameters for route `' + name + '`.');
				}

				return uri;
			}
		}

		throw new Error("No such route `" + name + "`");
	}

	/**
	 * @param {string} routeName
	 * @param {Object.<string, string>} routeParams
	 * @param {Object.<string, string>} queryParams
	 * @param {string} fragmentIdentifier
	 * @return {string}
	 */
	generateUrl(routeName, routeParams = {}, queryParams = {}, fragmentIdentifier = '')
	{
		let url = this.basePath.replace(/\/$/, '') + this.generateUri(routeName, routeParams);
		const query = (new URLSearchParams(queryParams)).toString();

		if (query != '') {
			url += '?' + query;
		}

		if (fragmentIdentifier) {
			url += '#' + fragmentIdentifier;
		}

		return url;
	}

	navigate(path = '')
	{
		window.history.pushState(null, null, path);
		this.dispatch();
	}

	/**
	 * @param {string} path
	 * @return {Router~RouteResult}
	 */
	match(path)
	{
		for (let i in this.routes) {
			const route = this.routes[i];
			const regexpPath = route.path.replace(/{([^:}]+)(:[^}]+)?}/g, function (match, p1, p2) {
				let key = p1;
				let pattern = p2 ? p2.substring(1) : '[^/]+';

				return "(?<" + key + ">" + pattern + ")";
			});
			const match = path.match(new RegExp("^" + regexpPath + "$"));
			if (match) {
				return {
					matchedRoute: route,
					matchedParams: match.groups
				};
			}
		}

		return undefined;
	}

	dispatch()
	{
		const url = new URL(window.location.href);
		const path = ('/' + this.trimSlashes(decodeURI(url.pathname))).replace(this.basePath, '/');
		const queryParams = url.searchParams;
		const fragmentIdentifier = url.hash.replace(/^#/, '');

		if (this.current === path && this.queryParams.toString() === queryParams.toString()) return;
		this.current = path;
		this.queryParams = queryParams;

		const routeResult = this.match(path);

		if (routeResult) {
			routeResult.matchedRoute.callback(
				routeResult.matchedParams,
				queryParams,
				fragmentIdentifier
			);
		} else {
			throw new Error("Path not found");
		}
	}
}

export {Router};
